import { useMutation } from '@tanstack/react-query';

import { AxiosError, AxiosResponse, AxiosInstance } from 'axios';
import { QueryKeys } from '../enums/QueryKeys';
import {
  MutationOptions,
  ReadingsExportDto,
  FormatAcceptHeaders,
  ReadingsExportRequest,
  MutationResult,
  AggregationOptions,
} from '@thingslog/repositories';
import DeviceSensorsDto from '@thingslog/repositories/src/deviceSensorsDto/DeviceSensorsDto';

interface useMultipleReadingsExportVariables {
  body: ReadingsExportRequest;
  fetchType?: AggregationOptions;
}

export interface MultipleReadingsExportQueryClient {
  getMultipleReadingsExportCSV: ({
    body,
  }: useMultipleReadingsExportVariables) => Promise<ArrayBuffer>;
  getMultipleReadingsExportExcel: ({
    body,
  }: useMultipleReadingsExportVariables) => Promise<ArrayBuffer>;
  getMultipleReadingsExportJSON: ({
    body,
  }: useMultipleReadingsExportVariables) => Promise<ReadingsExportDto[]>;
  useMultipleReadingsExportCSV: (
    options?: MutationOptions<ArrayBuffer, useMultipleReadingsExportVariables>
  ) => MutationResult<ArrayBuffer, useMultipleReadingsExportVariables>;
  useMultipleReadingsExportExcel: (
    options?: MutationOptions<ArrayBuffer, useMultipleReadingsExportVariables>
  ) => MutationResult<ArrayBuffer, useMultipleReadingsExportVariables>;
  useMultipleReadingsExportJSON: (
    options?: MutationOptions<ReadingsExportDto[], useMultipleReadingsExportVariables>
  ) => MutationResult<ReadingsExportDto[], useMultipleReadingsExportVariables>;
  useMultipleReadingsJSON: (
    fromDate: Date,
    toDate: Date,
    options?: MutationOptions<ReadingsExportDto[], DeviceSensorsDto[]>
  ) => MutationResult<ReadingsExportDto[], DeviceSensorsDto[]>;
}

export function createMultipleReadingsExportQueryClient(
  axios: AxiosInstance
): MultipleReadingsExportQueryClient {
  return new MultipleReadingsExportImp(axios);
}

class MultipleReadingsExportImp {
  public constructor(private axios: AxiosInstance) {}

  public getMultipleReadingsExportCSV = async ({
    body,
    fetchType,
  }: useMultipleReadingsExportVariables): Promise<ArrayBuffer> => {
    let acceptHeader = FormatAcceptHeaders.CSV;

    return this.axios
      .post('/api/readings-export', body, {
        headers: { Accept: acceptHeader },
        responseType: 'arraybuffer',
        params: { fetchType },
      })
      .then((response: AxiosResponse<ArrayBuffer>) => {
        return response.data;
      });
  };

  public getMultipleReadingsExportExcel = async ({
    body,
    fetchType,
  }: useMultipleReadingsExportVariables): Promise<ArrayBuffer> => {
    let acceptHeader = FormatAcceptHeaders.XLSX;

    return this.axios
      .post('/api/readings-export', body, {
        headers: { Accept: acceptHeader },
        responseType: 'arraybuffer',
        params: { fetchType },
      })
      .then((response: AxiosResponse<ArrayBuffer>) => {
        return response.data;
      });
  };

  public getMultipleReadingsExportJSON = async ({
    body,
    fetchType,
  }: useMultipleReadingsExportVariables): Promise<ReadingsExportDto[]> => {
    let acceptHeader = FormatAcceptHeaders.JSON;

    return this.axios
      .post('/api/readings-export', body, {
        headers: { Accept: acceptHeader },
        params: { fetchType },
      })
      .then((response: AxiosResponse<ReadingsExportDto[]>) => {
        return response.data;
      });
  };

  public useMultipleReadingsExportCSV = (
    options?: MutationOptions<ArrayBuffer, useMultipleReadingsExportVariables>
  ): MutationResult<ArrayBuffer, useMultipleReadingsExportVariables> => {
    return useMutation<ArrayBuffer, AxiosError, useMultipleReadingsExportVariables>(
      [QueryKeys.MultipleReadingsExport, 'CSV'],
      (variables: useMultipleReadingsExportVariables) =>
        this.getMultipleReadingsExportCSV({ ...variables }),
      options
    );
  };

  public useMultipleReadingsExportExcel = (
    options?: MutationOptions<ArrayBuffer, useMultipleReadingsExportVariables>
  ): MutationResult<ArrayBuffer, useMultipleReadingsExportVariables> => {
    return useMutation<ArrayBuffer, AxiosError, useMultipleReadingsExportVariables>(
      [QueryKeys.MultipleReadingsExport, 'Excel'],
      (variables: useMultipleReadingsExportVariables) =>
        this.getMultipleReadingsExportExcel({ ...variables }),
      options
    );
  };

  public useMultipleReadingsJSON = (
    fromDate: Date,
    toDate: Date,
    options?: MutationOptions<ReadingsExportDto[], DeviceSensorsDto[]>
  ): MutationResult<ReadingsExportDto[], DeviceSensorsDto[]> => {
    return useMutation<ReadingsExportDto[], AxiosError, DeviceSensorsDto[]>(
      [QueryKeys.MultipleReadingsExport, 'JSON'],
      (variables: DeviceSensorsDto[]) =>
        this.getMultipleReadingsExportJSON({
          body: { fromDate, toDate, deviceSensors: variables, deviceGroupIds: [] },
        }),
      options
    );
  };

  public useMultipleReadingsExportJSON = (
    options?: MutationOptions<ReadingsExportDto[], useMultipleReadingsExportVariables>
  ): MutationResult<ReadingsExportDto[], useMultipleReadingsExportVariables> => {
    return useMutation<ReadingsExportDto[], AxiosError, useMultipleReadingsExportVariables>(
      [QueryKeys.MultipleReadingsExport, 'JSON'],
      (variables: useMultipleReadingsExportVariables) =>
        this.getMultipleReadingsExportJSON({ ...variables }),
      options
    );
  };
}
